/**
 * Inline Console (ILC): on-page log panel for sessions where DevTools is unavailable
 * (mobile browsers, in-app WebViews). Patches console + XMLHttpRequest to mirror output
 * at the bottom of the page. Opt-in only; see Dashboard Debug Mode tile or `/enableILC`.
 */
let ilcAlreadyEnabled = false;
/** Set while ILC is active; call to restore console/XHR and remove the panel. */
let shutdownInlineConsole = null;

/** Fired on `window` when ILC opens or closes (`detail.open`). */
export const INLINE_CONSOLE_CHANGED_EVENT = "lyw-inline-console-changed";

function notifyInlineConsoleChanged(open) {
    if (typeof window !== "undefined") {
        window.dispatchEvent(
            new CustomEvent(INLINE_CONSOLE_CHANGED_EVENT, { detail: { open } })
        );
    }
}

/** Whether the inline console is currently active (panel mounted and hooks installed). */
export function isInlineConsoleEnabled() {
    return ilcAlreadyEnabled;
}

const ILC_EVAL_MAX_PRINT = 12000;
const ILC_EVAL_MAX_DEPTH = 4;
const ILC_EVAL_MAX_KEYS = 60;

/**
 * Pretty-print a value for the ILC (DevTools-style object trees are not replicated; this is a bounded preview).
 */
function formatIlcEvalResult(value) {
    if (value === undefined) {
        return "undefined";
    }
    if (value === null) {
        return "null";
    }
    const t = typeof value;
    if (t === "string") {
        return value;
    }
    if (t === "number" || t === "boolean" || t === "bigint") {
        return String(value);
    }
    if (t === "symbol") {
        return String(value);
    }
    if (t === "function") {
        const s = Function.prototype.toString.call(value);
        return s.length > ILC_EVAL_MAX_PRINT
            ? s.slice(0, ILC_EVAL_MAX_PRINT) + "\n…"
            : s;
    }
    if (t === "object") {
        const seen = new WeakSet();
        function preview(v, depth) {
            if (depth > ILC_EVAL_MAX_DEPTH) {
                return "[Max depth]";
            }
            if (v === undefined) {
                return "undefined";
            }
            if (v === null) {
                return null;
            }
            if (typeof v !== "object") {
                return v;
            }
            if (seen.has(v)) {
                return "[Circular]";
            }
            seen.add(v);
            if (Array.isArray(v)) {
                const slice = v.slice(0, 80);
                return slice.map((item) => preview(item, depth + 1));
            }
            const keys = Object.keys(v);
            const out = {};
            const limit = Math.min(keys.length, ILC_EVAL_MAX_KEYS);
            for (let i = 0; i < limit; i++) {
                const k = keys[i];
                try {
                    out[k] = preview(v[k], depth + 1);
                } catch {
                    out[k] = "[Error]";
                }
            }
            if (keys.length > ILC_EVAL_MAX_KEYS) {
                out["…"] = `${keys.length - ILC_EVAL_MAX_KEYS} more keys`;
            }
            return out;
        }
        try {
            const tree = preview(value, 0);
            let s = JSON.stringify(tree, null, 2);
            if (s.length > ILC_EVAL_MAX_PRINT) {
                s = s.slice(0, ILC_EVAL_MAX_PRINT) + "\n… (truncated)";
            }
            return s;
        } catch {
            try {
                return Object.prototype.toString.call(value);
            } catch {
                return "[Unprintable]";
            }
        }
    }
    return String(value);
}

/**
 * When a value is JSON (object/array or a string of JSON), returns one-line vs indented forms.
 * Returns null if not JSON or if compact === pretty (no point toggling).
 */
function getCompactAndPretty(value) {
    let parsed;
    if (typeof value === "string") {
        const t = value.trim();
        if (t.length < 2 || (t[0] !== "{" && t[0] !== "[")) {
            return null;
        }
        try {
            parsed = JSON.parse(t);
        } catch {
            return null;
        }
    } else if (value !== null && typeof value === "object") {
        parsed = value;
    } else {
        return null;
    }
    let compact;
    let pretty;
    try {
        compact = JSON.stringify(parsed);
        pretty = JSON.stringify(parsed, null, 2);
    } catch {
        return null;
    }
    if (compact === pretty) {
        return null;
    }
    return { compact, pretty };
}

/**
 * Appends a pre block plus a small Pretty print / Compact pill when useful.
 * @returns {boolean} true if the pretty UI was added
 */
function appendJsonWithPrettyToggle(container, value) {
    const pair = getCompactAndPretty(value);
    if (!pair) {
        return false;
    }
    const { compact, pretty } = pair;
    const pre = document.createElement("pre");
    pre.style.margin = "0";
    pre.style.flex = "1 1 auto";
    pre.style.minWidth = "0";
    pre.style.whiteSpace = "pre-wrap";
    pre.style.wordBreak = "break-word";
    pre.style.fontFamily = "inherit";
    pre.style.fontSize = "inherit";
    pre.textContent = compact;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = "Pretty print";
    btn.style.fontSize = "10px";
    btn.style.fontWeight = "700";
    btn.style.padding = "2px 8px";
    btn.style.cursor = "pointer";
    btn.style.borderRadius = "999px";
    btn.style.border = "1px solid #3d7a6a";
    btn.style.background = "#153028";
    btn.style.color = "#7dd3c0";
    btn.style.flexShrink = "0";
    btn.style.alignSelf = "flex-start";

    let prettyMode = false;
    btn.onclick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        prettyMode = !prettyMode;
        pre.textContent = prettyMode ? pretty : compact;
        btn.textContent = prettyMode ? "Compact" : "Pretty print";
    };

    const wrap = document.createElement("span");
    wrap.style.display = "flex";
    wrap.style.flexWrap = "wrap";
    wrap.style.alignItems = "flex-start";
    wrap.style.gap = "8px";
    wrap.style.maxWidth = "100%";
    wrap.appendChild(pre);
    wrap.appendChild(btn);
    container.appendChild(wrap);
    return true;
}

/** Pill button for long-entry expand/collapse (same family as Pretty print, different accent). */
function styleIlcExpandCollapseButton(el) {
    el.type = "button";
    el.style.fontSize = "10px";
    el.style.fontWeight = "700";
    el.style.padding = "2px 8px";
    el.style.cursor = "pointer";
    el.style.borderRadius = "999px";
    el.style.border = "1px solid #5a6a78";
    el.style.background = "#2a3038";
    el.style.color = "#b8c8dc";
    el.style.float = "right";
    el.style.flexShrink = "0";
    el.style.marginLeft = "8px";
    el.style.lineHeight = "1.2";
}

function styleToolbarButton(el) {
    el.type = "button";
    el.style.margin = "0";
    el.style.padding = "6px 12px";
    el.style.fontSize = "12px";
    el.style.fontFamily = "system-ui, sans-serif";
    el.style.fontWeight = "600";
    el.style.lineHeight = "1.2";
    el.style.borderRadius = "6px";
    el.style.border = "1px solid #555";
    el.style.background = "#3d3d3d";
    el.style.color = "#e8e8e8";
    el.style.cursor = "pointer";
    el.style.boxSizing = "border-box";
}

export function disableInlineConsole() {
    if (typeof shutdownInlineConsole === "function") {
        shutdownInlineConsole();
    }
}

export function enableInlineConsole() {
    if (ilcAlreadyEnabled) {
        return;
    }
    ilcAlreadyEnabled = true;

    let inlineConsole,
        logCount = 0,
        XMLHttpRequestCount = 0;
    const consoleHeight = 220,
        watchEvents = [],
        startMs = new Date().getTime(),
        oldLog = console.log,
        oldDebug = console.debug,
        oldWarn = console.warn,
        oldInfo = console.info,
        oldError = console.error,
        previousOnError = window.onerror,
        consoleWrapper = document.createElement("div");

    /** From: http://stackoverflow.com/questions/2234979/how-to-check-in-javascript-if-one-element-is-contained-within-another **/
    const isDescendant = function (parent, child) {
        let node = child.parentNode;
        while (node !== null) {
            if (node === parent) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    };

    const clearConsole = function () {
        if (inlineConsole) {
            inlineConsole.innerHTML = "";
        }
    };

    const toggleVisible = function (event, id, toggleElement) {
        const element = document.getElementById(id);
        const hiddenElement = document.getElementById(id + "-hidden");

        if (element.style.display) {
            element.style.display = null;
            hiddenElement.style.display = "none";
            toggleElement.textContent = "Hide";
            toggleElement.setAttribute("aria-expanded", "true");
        } else {
            element.style.display = "none";
            hiddenElement.style.display = null;
            toggleElement.textContent = "Show";
            toggleElement.setAttribute("aria-expanded", "false");
        }
    };

    const sendMsg = function (type, args, color) {
        const el = document.createElement("div"),
            eId = type + "-" + ++logCount,
            firstElementString =
                args.length === 0 || args[0] === undefined
                    ? ""
                    : JSON.stringify(args[0]),
            toggleElement = document.createElement("button"),
            hrElement = document.createElement("hr"),
            currentMs = new Date().getTime();
        let nElement;

        initInlineConsole();

        el.style.clear = "both";
        el.style.margin = "0";
        el.style.padding = "0";
        el.style.textAlign = "left";
        if (color) {
            el.style.color = color;
        }

        el.innerHTML +=
            "<strong>" +
            type +
            "</strong> [" +
            (currentMs - startMs) +
            'ms]: <span style="display:none;" id="' +
            eId +
            '-hidden">' +
            firstElementString.substring(0, 48) +
            "...</span>";

        if (args.length === 1) {
            nElement = document.createElement("span");
            nElement.id = eId;
            if (!appendJsonWithPrettyToggle(nElement, args[0])) {
                nElement.innerHTML = firstElementString;
            }
            el.appendChild(nElement);
        } else if (args.length > 1) {
            nElement = document.createElement("ol");
            nElement.id = eId;
            nElement.style.margin = "0";
            nElement.style.padding = "0";

            for (let i = 0, len = args.length, item; i < len; i++) {
                item = document.createElement("li");
                if (!appendJsonWithPrettyToggle(item, args[i])) {
                    item.innerHTML = JSON.stringify(args[i]);
                }
                nElement.appendChild(item);
            }

            el.appendChild(nElement);
        }
        if (firstElementString.length > 64 || args.length > 1) {
            styleIlcExpandCollapseButton(toggleElement);
            toggleElement.setAttribute("aria-expanded", "true");
            toggleElement.setAttribute(
                "aria-label",
                "Toggle full log entry visibility"
            );
            toggleElement.textContent = "Hide";
            toggleElement.onclick = function (event) {
                event.preventDefault();
                toggleVisible(event, eId, toggleElement);
            };
            el.appendChild(toggleElement);
        }

        hrElement.style.clear = "both";
        hrElement.style.margin = "0";
        hrElement.style.padding = "0";
        hrElement.style.borderColor = "#333";
        el.appendChild(hrElement);

        inlineConsole.appendChild(el);
        if (el.clientHeight > consoleHeight) {
            toggleVisible(null, eId, toggleElement);
        }
        inlineConsole.scrollTop = inlineConsole.scrollHeight;
    };

    const initInlineConsole = function () {
        if (!inlineConsole) {
            inlineConsole = document.createElement("div");

            const headerBar = document.createElement("div");
            headerBar.style.display = "flex";
            headerBar.style.alignItems = "center";
            headerBar.style.justifyContent = "space-between";
            headerBar.style.gap = "8px";
            headerBar.style.padding = "8px 10px";
            headerBar.style.background = "#2a2a2a";
            headerBar.style.borderBottom = "1px solid #444";

            const titleEl = document.createElement("span");
            titleEl.textContent = "Inline Console";
            titleEl.style.fontFamily = "ui-monospace, monospace";
            titleEl.style.fontSize = "14px";
            titleEl.style.fontWeight = "700";
            titleEl.style.color = "#e8e8e8";

            const btnGroup = document.createElement("div");
            btnGroup.style.display = "flex";
            btnGroup.style.alignItems = "center";
            btnGroup.style.gap = "8px";
            btnGroup.style.flexShrink = "0";

            const clearButton = document.createElement("button");
            clearButton.textContent = "Clear log";
            styleToolbarButton(clearButton);
            clearButton.onclick = clearConsole;

            const closeButton = document.createElement("button");
            closeButton.textContent = "Close";
            styleToolbarButton(closeButton);
            closeButton.style.borderColor = "#7a5c5c";
            closeButton.style.background = "#4a3535";
            closeButton.setAttribute("aria-label", "Close inline console");
            closeButton.onclick = function () {
                disableInlineConsole();
            };

            btnGroup.appendChild(clearButton);
            btnGroup.appendChild(closeButton);
            headerBar.appendChild(titleEl);
            headerBar.appendChild(btnGroup);

            const noteInput = document.createElement("input");
            noteInput.type = "text";
            noteInput.setAttribute("autocomplete", "off");
            noteInput.setAttribute(
                "aria-label",
                "Evaluate a JavaScript expression (like the browser console)"
            );
            noteInput.placeholder = "Expression, then Enter (e.g. window, 1+1, document.title)…";
            noteInput.style.width = "100%";
            noteInput.style.boxSizing = "border-box";
            noteInput.style.margin = "0";
            noteInput.style.padding = "8px 10px";
            noteInput.style.fontSize = "13px";
            noteInput.style.fontFamily = "ui-monospace, monospace";
            noteInput.style.border = "none";
            noteInput.style.borderTop = "1px solid #444";
            noteInput.style.background = "#1e1e1e";
            noteInput.style.color = "#d4d4d4";
            noteInput.style.outline = "none";

            noteInput.addEventListener("keydown", function (e) {
                if (e.key !== "Enter") return;
                const src = noteInput.value.trim();
                if (!src) return;
                e.preventDefault();
                try {
                    // Indirect eval → global scope in browsers (same idea as typing in the console).
                    const result = (0, eval)(src);
                    sendMsg("EVAL", [src, formatIlcEvalResult(result)], "#ce9178");
                    oldLog.apply(console, [result]);
                } catch (err) {
                    const msg = err && err.message ? err.message : String(err);
                    sendMsg("EVAL-ERR", [src, msg], "#f48771");
                    oldError.apply(console, [err]);
                }
                noteInput.value = "";
            });

            const inputRow = document.createElement("div");
            inputRow.style.display = "flex";
            inputRow.style.alignItems = "stretch";
            inputRow.appendChild(noteInput);

            consoleWrapper.appendChild(headerBar);
            consoleWrapper.appendChild(inlineConsole);
            consoleWrapper.appendChild(inputRow);

            consoleWrapper.style.backgroundColor = "#333333";
            consoleWrapper.style.color = "#cccccc";
            consoleWrapper.style.position = "fixed";
            consoleWrapper.style.bottom = "0";
            consoleWrapper.style.right = "0";
            consoleWrapper.style.left = "0";
            consoleWrapper.style.clear = "both";
            consoleWrapper.style.zIndex = "2147483646";
            consoleWrapper.style.boxShadow = "0 -4px 24px rgba(0,0,0,0.35)";
            consoleWrapper.classList.add("inline-console");

            inlineConsole.style.backgroundColor = "black";
            inlineConsole.style.border = "0";
            inlineConsole.style.color = "#00ff00";
            inlineConsole.style.height = consoleHeight + "px";
            inlineConsole.style.resize = "none";
            inlineConsole.style.overflowY = "auto";
            inlineConsole.style.overflowX = "hidden";
            inlineConsole.style.fontFamily = "ui-monospace, monospace";
            inlineConsole.style.fontSize = "12px";
        }
    };

    shutdownInlineConsole = function () {
        if (!ilcAlreadyEnabled) {
            return;
        }

        console.log = oldLog;
        console.debug = oldDebug;
        console.warn = oldWarn;
        console.info = oldInfo;
        console.error = oldError;

        if (XMLHttpRequest.prototype.oldOpen) {
            XMLHttpRequest.prototype.open = XMLHttpRequest.prototype.oldOpen;
            delete XMLHttpRequest.prototype.oldOpen;
        }
        if (XMLHttpRequest.prototype.oldSend) {
            XMLHttpRequest.prototype.send = XMLHttpRequest.prototype.oldSend;
            delete XMLHttpRequest.prototype.oldSend;
        }
        if (XMLHttpRequest.prototype.getId) {
            delete XMLHttpRequest.prototype.getId;
        }

        window.onerror = previousOnError;

        if (consoleWrapper.parentNode) {
            consoleWrapper.parentNode.removeChild(consoleWrapper);
        }

        ilcAlreadyEnabled = false;
        shutdownInlineConsole = null;
        notifyInlineConsoleChanged(false);
    };

    console.log = function (_message) {
        sendMsg("LOG", arguments);
        oldLog.apply(console, arguments);
    };

    console.debug = function (_message) {
        sendMsg("DEBUG", arguments, "#cccccc");
        oldDebug.apply(console, arguments);
    };

    console.warn = function (_message) {
        sendMsg("WARN", arguments, "#ff9900");
        oldWarn.apply(console, arguments);
    };

    console.info = function (_message) {
        sendMsg("INFO", arguments, "#0066ff");
        oldInfo.apply(console, arguments);
    };

    console.error = function (_message) {
        sendMsg("ERROR", arguments, "#ff0000");
        oldError.apply(console, arguments);
    };

    for (let ei = 0, eLen = watchEvents.length; ei < eLen; ei++) {
        document.addEventListener(
            watchEvents[ei],
            function (e) {
                if (!isDescendant(consoleWrapper, e.target)) {
                    sendMsg(
                        "EVENT",
                        [
                            "An event type &quot;" +
                                e.type +
                                "&quot; was triggered by a &quot;" +
                                e.target.nodeName +
                                "&quot; node.",
                        ],
                        "#cccccc"
                    );
                }
            },
            true
        );
    }

    window.onerror = function (message, lineNumber, file) {
        sendMsg("ERROR", [message, lineNumber, file], "#ff0000");
        if (typeof previousOnError === "function") {
            return previousOnError.apply(this, arguments);
        }
        return false;
    };

    initInlineConsole();
    document.body.appendChild(consoleWrapper);

    XMLHttpRequest.prototype.oldOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.oldSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.getId = function () {
        if (!this.threadId) {
            this.threadId = ++XMLHttpRequestCount;
        }
        return ("00" + this.threadId).slice(-3);
    };

    XMLHttpRequest.prototype.open = function (
        method,
        url,
        async,
        user,
        password
    ) {
        sendMsg(
            "HTTP",
            [
                "Opening Connection #" + this.getId(),
                {
                    method: method,
                    url: url,
                    async: async,
                    user: user,
                    password: password,
                },
            ],
            "#cccccc"
        );
        this.oldOpen(method, url, async, user, password);
    };

    XMLHttpRequest.prototype.send = function (a) {
        this.addEventListener(
            "load",
            function () {
                sendMsg(
                    "HTTP",
                    [
                        "Connection #" +
                            this.getId() +
                            " Completed Successfully: " +
                            this.status,
                    ],
                    "#cccccc"
                );
            },
            false
        );
        this.addEventListener(
            "error",
            function () {
                sendMsg(
                    "HTTP-ERROR",
                    ["Connection #" + this.getId() + " Failed: " + this.status],
                    "#ff0000"
                );
            },
            false
        );

        this.oldSend(a);
    };

    console.log("Inline console enabled.");
    notifyInlineConsoleChanged(true);
}
